import React, { useRef, useState } from "react";
import cn from "./cn";
import { fractionAt } from "./videoPlayerRules";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * VideoSlider — the seek bar and the volume bar of `VideoPlayer`. Not in the
 * barrel: a page that wants a range control wants a form control, not this.
 *
 * Dragging works with a mouse, a finger or a pen, while the video plays. The
 * bar takes pointer capture on press, so a thumb that slides off the bar keeps
 * scrubbing, and it sets `touch-action: none`, so the drag is not stolen by the
 * page scrolling. The hit area is 24px tall around a 4px track.
 *
 * Keys follow the ARIA slider pattern: arrows by `step`, Page Up / Down by
 * `bigStep`, Home / End to the ends. A key it uses is `preventDefault`ed, which
 * is how the player knows not to read the same arrow as its own shortcut.
 *
 * Props:
 *   label      — the accessible name ("Seek", "Volume")
 *   value, max — numbers in the same unit (seconds; 0…1)
 *   buffered   — optional, drawn as a lighter bar behind the value
 *   live       — commit on every move (volume). Off, a drag only PREVIEWS
 *                (`onScrub`) and commits once on release (seek) — a seek per
 *                pixel would ask the server for a new range per pixel.
 *   onCommit(v), onScrub(v | null)
 *   valueText(v) — what a screen reader speaks for a value
 *   preview(v)   — a label shown above the bar while dragging or hovering
 */
const VideoSlider = ({
  label,
  value,
  max,
  buffered = 0,
  live = false,
  step,
  bigStep,
  onCommit,
  onScrub,
  valueText,
  preview,
  className,
}) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  const usable = max > 0;

  const valueAt = (clientX) => fractionAt(clientX, trackRef.current?.getBoundingClientRect()) * max;

  const move = (v) => {
    setDrag(v);
    onScrub?.(v);
    if (live) onCommit(v);
  };

  const onPointerDown = (e) => {
    if (!usable || (e.pointerType === "mouse" && e.button !== 0)) return;
    draggingRef.current = true;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is a nicety */
    }
    move(valueAt(e.clientX));
  };

  const onPointerMove = (e) => {
    if (draggingRef.current) move(valueAt(e.clientX));
    else if (e.pointerType === "mouse" && usable) setHover(valueAt(e.clientX));
  };

  const finish = (e, commit) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const v = commit ? valueAt(e.clientX) : null;
    setDrag(null);
    onScrub?.(null);
    if (commit) onCommit(v);
  };

  const onKeyDown = (e) => {
    if (!usable) return;
    const next = {
      ArrowRight: value + step,
      ArrowUp: value + step,
      ArrowLeft: value - step,
      ArrowDown: value - step,
      PageUp: value + bigStep,
      PageDown: value - bigStep,
      Home: 0,
      End: max,
    }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    onCommit(clamp(next, 0, max));
  };

  const shown = drag ?? value;
  const pct = usable ? clamp((shown / max) * 100, 0, 100) : 0;
  const bufferedPct = usable ? clamp((buffered / max) * 100, 0, 100) : 0;
  const tip = drag ?? hover;
  const active = drag !== null;

  return (
    <div
      role="slider"
      tabIndex={usable ? 0 : -1}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(max) || 0}
      aria-valuenow={Math.round(shown) || 0}
      aria-valuetext={valueText ? valueText(shown) : undefined}
      aria-disabled={!usable || undefined}
      className={cn(
        "tw-group/slider tw-relative tw-flex tw-h-6 tw-touch-none tw-items-center tw-rounded-sm tw-outline-none focus-visible:tw-shadow-focus",
        usable ? "tw-cursor-pointer" : "tw-cursor-default",
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => finish(e, true)}
      onPointerCancel={(e) => finish(e, false)}
      onPointerLeave={() => setHover(null)}
      onKeyDown={onKeyDown}
    >
      <div
        ref={trackRef}
        className={cn(
          "tw-relative tw-w-full tw-rounded-full tw-bg-on-surface-dark/30 tw-transition-[height] tw-duration-100",
          active ? "tw-h-1.5" : "tw-h-1 group-hover/slider:tw-h-1.5",
        )}
      >
        {bufferedPct > 0 && (
          <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-rounded-full tw-bg-on-surface-dark/45" style={{ width: `${bufferedPct}%` }} />
        )}
        <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-rounded-full tw-bg-primary-solid" style={{ width: `${pct}%` }} />
        <div
          className={cn(
            "tw-absolute tw-top-1/2 tw-h-3 tw-w-3 tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-full tw-bg-on-surface-dark tw-shadow-card tw-transition-transform",
            active ? "tw-scale-125" : "group-hover/slider:tw-scale-110",
          )}
          style={{ left: `${pct}%` }}
        />
      </div>
      {preview && tip !== null && (
        <span
          className="tw-pointer-events-none tw-absolute tw-bottom-full tw-mb-1.5 tw--translate-x-1/2 tw-whitespace-nowrap tw-rounded-sm tw-bg-surface-dark/90 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-on-surface-dark"
          style={{ left: `clamp(1.75rem, ${usable ? (tip / max) * 100 : 0}%, calc(100% - 1.75rem))` }}
        >
          {preview(tip)}
        </span>
      )}
    </div>
  );
};

export default VideoSlider;
